import { completeInspectionResults } from "../../api/InspectionResults";
import { getWorkOrder } from "../../api/WorkOrders";
import { useWorkOrderStore } from "../../store/wo-order";
import { getCurrentPlant } from "../../util/ApiOptionsHelper";
import { getUserDisplayName, getUserId } from "../../util/UserHelper";
import { WorkOrder } from "../workorders/WorkOrder";

export const saveInspection = async (
  woNumber: string,
  inspectionId: string,
  pointValues: PointValue[],
  inspectionDef: InspectionDef,
  failedFlag: boolean,
  comments?: string,
  testEquipment?: string,
  pointImages?: { [key: string]: InspectionPointImage[] },
  pointComments?: { [key: string]: string },
  equipmentId?: string,
  isEquipmentContext?: boolean
) => {
  const data = await getInspectionResultsData(
    woNumber,
    inspectionId,
    pointValues,
    inspectionDef,
    failedFlag,
    comments,
    testEquipment,
    pointImages,
    pointComments,
    equipmentId,
    isEquipmentContext
  );

  return await completeInspectionResults(data);
};

export const getInspectionResultsData = async (
  woNumber: string,
  inspectionId: string,
  pointValues: PointValue[],
  inspectionDef: InspectionDef,
  failedFlag: boolean,
  comments?: string,
  testEquipment?: string,
  pointImages?: { [key: string]: InspectionPointImage[] },
  pointComments?: { [key: string]: string },
  equipmentId?: string,
  isEquipmentContext?: boolean
) => {
  let workOrder: WorkOrder | undefined = undefined;
  if (!isEquipmentContext) {
  const cachedWo = useWorkOrderStore.getState().getWorkOrderById(woNumber);
  if (cachedWo) {
    //got it from the cache
    workOrder = {
      ...cachedWo,
      EquipmentMaster: { ...cachedWo.EquipmentMaster, Serial: null },
    };
  }
  if (!workOrder) {
    //load WO
    const respWo = await getWorkOrder({
      woNumber: woNumber,
      equipmentMaster: true,
      inspectionDefinition: true,
    });
    respWo.data.EquipmentMaster.Serial = null;
    workOrder = respWo.data;
  }
  }
  const empName = await getUserDisplayName();
  const userId = await getUserId();
  const plant = await getCurrentPlant();
  const enrichedPointValues = pointValues.map((point) => {
    if (point.Images !== undefined && point.Comments !== undefined) {
      return point;
    } else {
      const key = `${point.PointId}_${point.ObjectId}`;
      return {
        ...point,
        Images: pointImages?.[key] || [],
        Comments: pointComments?.[key] || "",
      };
    }
  });
  const isEqContext = !!isEquipmentContext;
  const data: InspectionResultsData = {
    Comments: comments!,
    Definition: inspectionDef,
    DocumentId: 0,
    EmployeeName: empName!,
    Equipment: isEqContext ? equipmentId! : workOrder?.Equipment!,
    EquipmentLocation: isEqContext ? undefined : workOrder?.EquipmentMaster,
    FailedFlag: failedFlag,
    InspectedDatetime: new Date().toISOString(),
    InspectionId: +inspectionId,
    Manufacturer: null,
    ManufacturerSerialId: null,
    Model: null,
    ObjectId: isEqContext
      ? `${plant}|${inspectionId}|0`
      : `${plant}|${workOrder?.InspectionId}|0`,
    Plant: plant,
    Points: enrichedPointValues,
    ResultId: 0,
    RowVersion: null!,
    Serial: null,
    SerialId: "",
    SourceTableName: "ic_result",
    TestEquipment: !testEquipment ? [] : JSON.parse(testEquipment),
    UserId: userId!,
    WoBase: isEqContext ? "" : workOrder?.WoBase!,
    WoSubtask: isEqContext ? "" : workOrder?.WoSubtask!,
    WoTask: isEqContext ? "" : workOrder?.WoTask!,
  };

  return data;
};

export interface InspectionDef {
  InspectionId: number;
  Description: string;
  Pland: string;
  ObjectId: string;
  MaterialRequirements: string;
  Instructions: string;
  InspectionType: string;
  Points: PointDefType[];
  EquipmentInspections: any[];
  Elements: any[];
}

export interface PointDefType {
  PointId: number;
  RowOrder: number;
  ObjectId: string;
  Description: string;
  LongDescription?: string;
  RequiredFlag: boolean;
  ValueDataType: string;
  Label: string;
  Category: string;
  DefaultValue: string;
  Precision?: number;
  HighValue: number;
  LowValue: number;
  Limit: boolean;
  Equipment: string;
  DesiredValue: number;
  Tolerance: boolean;
  TolerancePercent: boolean;
  Image?: InspectionPointImage[];
  Images?: InspectionPointImage[];
  Meta: [
    {
      MetaValue: number;
      MetaType: string;
      MetaSubtype: string;
    }
  ];
  Columns: [
    {
      ImpliesFailureFlag: boolean;
      ColumnOrder: number;
      ColumnCode: string;
      ObjectId: string;
      Failed: boolean;
      PointId: number;
    }
  ];
}

export interface PointValue {
  PointId: number;
  ObjectId: string;
  ColumnCode: string;
  Measure: string;
  FailedFlag: boolean;
  Images?: InspectionPointImage[];
  Comments?: string;
  ResultId?: number;
}

export interface PointEqDescription {
  Equipment: string;
  Description: string;
}

export interface InspectionResultsData {
  Comments: string;
  Definition: any;
  DocumentId: number;
  EmployeeName: string;
  Equipment: string;
  EquipmentLocation?: any;
  FailedFlag: boolean;
  InspectedDatetime: string;
  InspectionId: number;
  Manufacturer: any;
  ManufacturerSerialId: any;
  Model: any;
  ObjectId: string;
  Plant: string;
  Points: PointValue[];
  ResultId: number;
  RowVersion: string;
  Serial: any;
  SerialId?: string;
  SourceTableName?: string;
  TestEquipment: [];
  UserId: string;
  WoBase: string;
  WoSubtask: string;
  WoTask: string;
}

export interface LocalInspectionsData {
    PointEqpList: {};
    ActionResult: {
        MessageList: any[];
        Success: boolean;
    };
    Equipment: string;
    InspectionId: string;
    InspectionResultData: InspectionResultsData;
    Messages: any[];
    PrevStatus: string;
    Status: string;
    WorkOrder: string;
    // User Story 889870: epoch-ms of last write, used for TTL/LRU pruning of
    // locally stored inspection drafts. Optional for backward compatibility
    // with entries saved before this field existed.
    SavedAt?: number;
}

export interface InspectionPointImage {
  ImageType: string;
  ImageExtension: string;
  AltText: string;
  InspectionPointImage?: string;
  ReadOnly?: boolean;
}